import { useGetMyBookingsQuery } from '@massilia/queries'
import { Button } from '@massilia/ui'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Loader2, MailIcon, PhoneIcon, Plus } from 'lucide-react'
import { type ReactNode, useState } from 'react'

import { BookingList } from '@/components/booking-list/booking-list'
import { ImportReservationDialog } from '@/components/import-reservation/import-reservation-dialog'
import { CloudyCopiableContactCard } from '@/components/payment-confirmation/cloudy-copiable-contact-card'
import {
  WhatsappCallCloudyCard,
  WhatsappMessageCloudyCard,
} from '@/components/payment-confirmation/whatsapp-components'
import { AGENCY_EMAIL, AGENCY_PHONE } from '@/lib/constants/agency'

export const Route = createFileRoute('/_authenticated/_layout/')({
  component: RouteComponent,
  staticData: {
    hero: {
      subtitle: 'Ajoutez vos réservations à votre compte ou modifiez vos données personnelles',
    },
  },
})

export function RouteComponent() {
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false)
  const { data: bookings = [], isLoading: isLoadingBookings } = useGetMyBookingsQuery()

  let bookingSection: ReactNode
  if (isLoadingBookings) {
    bookingSection = (
      <div className="flex items-center justify-center py-14">
        <Loader2 className="size-8 animate-spin text-brand-600" />
      </div>
    )
  } else if (bookings.length > 0) {
    bookingSection = (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col items-center justify-between md:flex-row">
          <h2 className="text-xl font-semibold text-gray-light-900">Mes réservations</h2>
          <Button
            className="gap-1 bg-brand-600 text-white hover:bg-brand-700"
            onClick={() => setIsImportDialogOpen(true)}
          >
            <Plus className="size-5" />
            Importer une réservation
          </Button>
        </div>
        <BookingList bookings={bookings} />
      </div>
    )
  } else {
    bookingSection = (
      /* Empty State Card */
      <div className="flex flex-col items-center rounded-3xl border border-gray-light-200 px-6 py-14 md:px-8">
        <img
          alt="Avion"
          className="mb-6 h-38 w-61 max-w-full rounded-4xl object-cover"
          src="/images/empty-state-airplane.jpg"
        />
        <h2 className="mb-6 text-center text-base font-semibold text-gray-light-900">
          Vous n&apos;avez pas encore de réservation
        </h2>
        <Button
          className="gap-1 bg-brand-600 text-white hover:bg-brand-700"
          onClick={() => setIsImportDialogOpen(true)}
        >
          <Plus className="size-5" />
          Importer une réservation
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-1 flex-col">
      {/* Content Area — le héro est rendu par le layout (staticData.hero) */}
      <main className="flex flex-1 flex-col gap-8 bg-white px-4 py-8 md:px-28 md:py-10">
        {/* Booking Section */}
        {bookingSection}

        {/* Contact + CTA Section */}
        <div className="flex flex-col gap-6 lg:flex-row">
          {/* Contact Cards Grid — deux parts contre une : quatre cartes dans une
              moitié d'écran comprimaient chaque libellé. */}
          <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2 lg:flex-[2]">
            <CloudyCopiableContactCard
              icon={PhoneIcon}
              label="Téléphone"
              title="Nous appeler !"
              value={AGENCY_PHONE}
            />
            <CloudyCopiableContactCard
              icon={MailIcon}
              label="Adresse email"
              title="Par email"
              value={AGENCY_EMAIL}
            />
            <WhatsappCallCloudyCard />
            <WhatsappMessageCloudyCard />
          </div>

          {/* CTA Card — les nuages sont un décor : `z-0` et une taille bornée,
              sinon le texte blanc passait par-dessus le nuage blanc et devenait
              illisible. Le bouton vient après la phrase qui l'explique. */}
          <div className="relative flex min-h-64 flex-col justify-end overflow-hidden rounded-2xl bg-brand-500 p-5 sm:p-6 lg:flex-1">
            <img
              alt=""
              aria-hidden
              className="pointer-events-none absolute top-0 left-0 z-0 max-w-[55%]"
              src="/icons/top-left-cloud.svg"
            />
            <img
              alt=""
              aria-hidden
              className="pointer-events-none absolute right-0 bottom-0 z-0 max-w-[45%]"
              src="/icons/bottom-right-cloud.svg"
            />
            <img
              alt=""
              aria-hidden
              className="relative z-10 mb-4 w-40 max-w-full sm:w-48"
              src="/images/plane.svg"
            />
            <div className="relative z-10 flex flex-col items-start gap-3">
              <h3 className="text-2xl font-semibold text-white">Une envie de voyage ?</h3>
              <p className="max-w-prose text-sm text-white">
                Trouvez la destination de vos rêves dès maintenant avec Massilia Voyages.
              </p>
              <Button asChild>
                <Link to="/recherche">Lancer une recherche</Link>
              </Button>
            </div>
          </div>
        </div>
      </main>

      {/* Import Reservation Dialog */}
      <ImportReservationDialog
        isOpen={isImportDialogOpen}
        onClose={() => setIsImportDialogOpen(false)}
      />
    </div>
  )
}
